import { useState } from 'react';
import { Button, Form, InputNumber, Radio } from 'antd';
import type { FormInstance } from 'antd';
import {
  dailyClassAdjustmentTierFor,
  normalizeDailyClassAdjustmentTiers,
  type DailyClassAdjustmentTier,
} from '@salaries/shared';

// התאמת שכר לפי כמות שיעורים ביום — a ± ₪-per-class ladder decided by HOW MANY
// classes the coach taught on the SAME day. Editor for the tier list plus (at
// box level) the on/off radio, shared by the box defaults and the per-staff
// "custom" section so the inputs stay identical.
//
// NOT marginal: unlike the volume bonus, only the SINGLE tier the day's count
// reaches applies — and it applies to EVERY class that day, not just the ones
// past the threshold. The little calculator at the bottom spells that out with
// real numbers, and the amount field allows negative values (a pure deduction).

/** The range label for a tier, from the ladder it lives in: "2–3 שיעורים ביום" /
 *  "מ-4 שיעורים ביום ואילך". Computed off the SORTED, deduped ladder so the
 *  rows can stay in whatever order they were typed. */
function rangeLabels(tiers: DailyClassAdjustmentTier[]): Map<number, string> {
  const sorted = normalizeDailyClassAdjustmentTiers(tiers);
  const map = new Map<number, string>();
  sorted.forEach((t, i) => {
    const next = sorted[i + 1];
    const to = next ? next.from_count - 1 : null;
    map.set(
      t.from_count,
      to == null
        ? `מ-${t.from_count} שיעורים ביום ואילך`
        : t.from_count === to
          ? `${t.from_count} שיעורים ביום`
          : `${t.from_count}–${to} שיעורים ביום`,
    );
  });
  return map;
}

export function DailyTiersInput({
  value = [],
  onChange,
}: {
  value?: DailyClassAdjustmentTier[];
  onChange?: (v: DailyClassAdjustmentTier[]) => void;
}) {
  const rows = value ?? [];
  const commit = (next: DailyClassAdjustmentTier[]) => onChange?.(next);
  const patch = (i: number, p: Partial<DailyClassAdjustmentTier>) =>
    commit(rows.map((r, idx) => (idx === i ? { ...r, ...p } : r)));
  const remove = (i: number) => commit(rows.filter((_, idx) => idx !== i));
  const add = () => {
    // Start the new tier one class above the highest one so far — the common
    // case is "…and from there on up".
    const nextFrom = rows.length ? Math.max(...rows.map((r) => r.from_count || 1)) + 1 : 1;
    commit([...rows, { from_count: nextFrom, adjustment: 0 }]);
  };

  const labels = rangeLabels(rows);
  // Two rows starting at the same count: only one of them can survive the save
  // (the last one entered wins), so say so instead of silently dropping money.
  const dup = rows.length !== new Set(rows.map((r) => r.from_count)).size;

  // Sample count for the calculator — seeded above the top tier so it shows the
  // ladder's last (usually most-relevant) bracket by default.
  const topFrom = rows.length ? Math.max(...rows.map((r) => r.from_count || 1)) : 0;
  const [sample, setSample] = useState<number | null>(null);
  const sampleCount = sample ?? Math.max(1, topFrom);
  const sampleTier = dailyClassAdjustmentTierFor(sampleCount, rows);
  const sampleAdjustment = sampleTier?.adjustment ?? 0;
  const sampleTotal = sampleAdjustment * sampleCount;

  return (
    <div>
      {rows.length > 0 && (
        <div className="border rounded mb-2">
          {rows.map((r, i) => (
            <div
              key={i}
              className="flex items-center gap-2 px-3 py-2 border-b last:border-b-0 flex-wrap"
            >
              <span className="text-secondary-text">מ-</span>
              <InputNumber
                min={1}
                max={1000}
                value={r.from_count}
                onChange={(v) => patch(i, { from_count: Math.max(1, Math.floor(Number(v) || 1)) })}
                dir="ltr"
                style={{ width: 70 }}
              />
              <span className="text-secondary-text">שיעורים ביום, התאמה של</span>
              <InputNumber
                suffix="₪"
                value={r.adjustment}
                onChange={(v) => patch(i, { adjustment: Number(v) || 0 })}
                dir="ltr"
                style={{ width: 110 }}
              />
              <span className="text-secondary-text">לשיעור</span>
              <span className="text-small text-secondary-text">({labels.get(r.from_count)})</span>
              <span className="flex-1" />
              <Button size="small" danger type="text" onClick={() => remove(i)}>
                הסר
              </Button>
            </div>
          ))}
        </div>
      )}

      <Button onClick={add} disabled={rows.length >= 20}>
        הוסף מדרגה
      </Button>

      {dup && (
        <div className="text-small mt-2" style={{ color: '#d4380d' }}>
          שתי מדרגות מתחילות מאותו מספר שיעורים — שימו לב כי רק האחרונה תישמר.
        </div>
      )}

      {rows.length > 0 && (
        <div className="mt-3 p-3 rounded bg-grey-bg">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-secondary-text">בדיקה: יום עם</span>
            <InputNumber
              min={1}
              max={1000}
              value={sampleCount}
              onChange={(v) => setSample(Math.max(1, Math.floor(Number(v) || 1)))}
              dir="ltr"
              style={{ width: 80 }}
            />
            <span className="text-secondary-text">שיעורים יקבל התאמה של</span>
            <span className="font-semibold">₪{sampleTotal.toLocaleString()}</span>
          </div>
          <div className="text-small text-secondary-text mt-1">
            {sampleTier
              ? `${sampleCount}×${sampleAdjustment}₪ (${labels.get(sampleTier.from_count)})`
              : 'לא נכנס לאף מדרגה — ללא התאמה'}
          </div>
        </div>
      )}
    </div>
  );
}

// showEnabled (box defaults): the on/off radio is offered. In the per-staff drawer
// (showEnabled=false) the source select already drives on/off, so only the
// ladder shows and the consumer forces enabled=true on save.
export function DailyClassAdjustmentFields({
  form,
  showEnabled = true,
}: {
  form: FormInstance;
  showEnabled?: boolean;
}) {
  const enabled = Form.useWatch('daily_class_adjustment_enabled', form);
  // The ladder field stays MOUNTED even while off, so turning it back on
  // doesn't come back to an empty ladder (and antd doesn't drop it on save).
  const tiers = (
    <div style={{ display: showEnabled && !enabled ? 'none' : undefined }} className="mt-2">
      <Form.Item name="daily_class_adjustment_tiers" className="mb-0">
        <DailyTiersInput />
      </Form.Item>
    </div>
  );
  if (!showEnabled) return tiers;
  return (
    <>
      <Form.Item name="daily_class_adjustment_enabled" className="mb-0">
        <Radio.Group className="flex flex-col gap-3">
          <Radio value={false}>ללא התאמה לפי כמות שיעורים ביום</Radio>
          <Radio value={true}>התאמת שכר לפי כמות השיעורים באותו יום</Radio>
        </Radio.Group>
      </Form.Item>
      {tiers}
    </>
  );
}
